import React from 'react';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './sass/Main.scss';
import { connect } from "react-redux";
import NetworkDetector from "./Layout/NetworkDetector";
import Admin from "./Layout/Admin";
import ErrorBoundary from './Components/ErrorBoundary';
import { PrivateRouteWithRedux } from "./PrivateRoute";
import { VerificationEmailScreensWithState } from "./Components/Auth/VerificationEmailScreen";
import { AuthRouter } from "./Components/Auth/AuthRouter";
import Terms from "./Components/Legal/Terms";
import TermsHi from "./Components/Legal/TermsHi";
import PrivacyPolicyHi from 'Components/Legal/PrivacyPolicyHi';
import Privacy from "./Components/Legal/Privacy";
import Faq from "./Components/Legal/Faq";
import FaqHi from "./Components/Legal/FaqHi";
import Support from "./Components/Legal/Support";
import Support_hi from "./Components/Legal/Support_hi";
import CookiePolicyHi from 'Components/Legal/CookiePolicyHi';
import CookiePolicy from 'Components/Legal/CookiePolicy';
import { ResetPasswordFormWithState } from "./Components/Auth/UpdateResetPasswordForm";
import { SurveyAttemptMessageWithState } from "./Components/Surveys/SurveyAlreadyAttempted";
import { SurveyUnavailableMessageWithState } from "./Components/Surveys/SurveyUnavailable";
import SurveyHold from 'Components/SurveyHold/SurveyHold';
import PartnerSurveyHold from 'Components/SurveyHold/PartnerSurveyHold';
import SurveyCallbackPage from './Components/Surveys/SurveyCallbackPage';
import SurveyThankYouPage from './Components/Surveys/SurveyThankYouPage';
import DXTestPreScreenQuesPage from './Components/DXTest/PreScreenQuesPage';
import PartnerSurvey from 'Components/Partners/PartnerSurvey';

class App extends React.Component<any, any> {
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;

  componentDidMount() {
    window.addEventListener('mousemove', this.handleActivity);
    window.addEventListener('keypress', this.handleActivity);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.handleActivity();
  }

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.handleActivity);
    window.removeEventListener('keypress', this.handleActivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearLogoutTimer();
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.handleActivity();
    }
  };

  handleActivity = () => {
    this.clearLogoutTimer();
    this.startLogoutTimer();
  };

  startLogoutTimer = () => {
    const role = this.props.adminUser?.adminUser?.role ?? "finance";
    // Minutes per role; an unlisted role falls back to 30 instead of NaN
    // (which made setTimeout fire immediately).
    const specialLogout = { "super_admin": 30, "admin": 30, "tl": 60, "pm": 60, "sales": 30, "panelist": 15, "finance": 15 };
    this.logoutTimer = setTimeout(() => {
      const SESSION_EXPIRY_MS = 10 * 60 * 1000;
      const lastUserActivity: any = localStorage.getItem("lastUserActivity");
      const now: any = Date.now();
      if (lastUserActivity && now - lastUserActivity > SESSION_EXPIRY_MS) {
        localStorage.clear();
        window.location.href = '/';
      } else {
        localStorage.setItem("lastUserActivity", now);
      }
    }, (specialLogout[role] || 30) * 60 * 1000);
  };

  clearLogoutTimer = () => {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  };

  render() {
    return (
      <Router basename='/'>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
        <NetworkDetector />
        <Switch>
          <Route path="/auth" component={() => <ErrorBoundary><AuthRouter /></ErrorBoundary>} />
          <Route path="/survey-attempted-message" component={SurveyAttemptMessageWithState} />
          <Route path="/survey-unavailable-message" component={SurveyUnavailableMessageWithState} />
          <Route path="/verify-screen" component={VerificationEmailScreensWithState} />
          <Route path='/terms-hi' component={TermsHi} />
          <Route path='/terms' component={Terms} />
          <Route path='/privacy-policy-hi' component={PrivacyPolicyHi} />
          <Route path='/privacy-policy' component={Privacy} />
          <Route path='/faq' component={Faq} />
          <Route path='/faq-hi' component={FaqHi} />
          <Route path='/survey-hold' component={SurveyHold} />
          <Route path='/partner-survey-hold' component={PartnerSurveyHold} />
          <Route path='/support' component={Support} />
          <Route path='/support-hi' component={Support_hi} />
          <Route path='/cookie-policy-hi' component={CookiePolicyHi} />
          <Route path='/cookie-policy' component={CookiePolicy} />
          <Route path='/reset-password/:token' component={ResetPasswordFormWithState} />
          {/* Public survey callback landing pages — respondent lands here after
              finishing a survey. No auth required and must be OUTSIDE the
              PrivateRouteWithRedux admin shell (no sidebar/navbar). */}
          <Route path='/surveys/completed' render={() => <SurveyCallbackPage status="completed" />} />
          <Route path='/surveys/terminate' render={() => <SurveyCallbackPage status="terminate" />} />
          <Route path='/surveys/overquota' render={() => <SurveyCallbackPage status="overquota" />} />
          <Route path='/surveys/qualityterminate' render={() => <SurveyCallbackPage status="qualityterminate" />} />
          {/* Static-callback thank-you pages — the client redirects here with
              ?userid=<respondent id> and no surveyid; the page records the
              outcome (backend resolves the survey from the userid), then
              shows the thank-you message. not-recorded never records. Must
              sit above the PrivateRouteWithRedux catch-all ("/"), which
              would otherwise send these to /auth/login. */}
          <Route path='/completed' render={() => <SurveyThankYouPage status="completed" />} />
          <Route path='/terminate' render={() => <SurveyThankYouPage status="terminate" />} />
          <Route path='/overquota' render={() => <SurveyThankYouPage status="overquota" />} />
          <Route path='/qualityterminate' render={() => <SurveyThankYouPage status="qualityterminate" />} />
          <Route path='/not-recorded' render={() => <SurveyThankYouPage status="not-recorded" />} />
          {/* Earlier /thank-you/* paths, kept working: they were published as
              static links before and may be configured on client platforms. */}
          <Route path='/thank-you/completed' render={() => <SurveyThankYouPage status="completed" />} />
          <Route path='/thank-you/terminate' render={() => <SurveyThankYouPage status="terminate" />} />
          <Route path='/thank-you/overquota' render={() => <SurveyThankYouPage status="overquota" />} />
          <Route path='/thank-you/qualityterminate' render={() => <SurveyThankYouPage status="qualityterminate" />} />
          <Route path='/thank-you/not-recorded' render={() => <SurveyThankYouPage status="not-recorded" />} />
          <Route path='/partner' component={PartnerSurvey} />
          {/* Partner entry is served by the admin backend at GET /p as a
              direct 302 redirect — no React route here. In dev,
              src/setupProxy.js forwards /p to the backend; in prod nginx
              does the same. */}
          {/* Brand-neutral runtime prescreener — public (no auth). Same
              component serves three flows; the backend evaluator detects
              panelist vs partner from user_id (DB lookup) and dispatches.
                /PreScreenQues/:surveyId/:userId         — panelist + PM-test
                /PartnerPreScreenQues/:surveyId/:userId  — vendor partner
              The legacy /PreScreenQues/:surveyId/:testUserId path is the
              same URL as the new /:userId mount and continues to work. */}
          <Route path='/PreScreenQues/:surveyId/:userId' component={DXTestPreScreenQuesPage} />
          <Route path='/PartnerPreScreenQues/:surveyId/:userId' component={DXTestPreScreenQuesPage} />
          <PrivateRouteWithRedux path="/admin" component={() => <ErrorBoundary><Admin /></ErrorBoundary>} />
          <PrivateRouteWithRedux path="/" component={() => <ErrorBoundary><Admin /></ErrorBoundary>} />
        </Switch>
      </Router>
    );
  }
}

const mapStateToProps = (state) => ({
  adminUser: state.adminUser,
});

const AppWithState = connect(mapStateToProps)(App);

export { AppWithState as App }
